import { useListEnvironmentInventory } from "@/queries/environment-queries";
import { useListProjects, useListProjectStates } from "@/queries/project-queries";
import { useListRunnerEnvironmentClasses } from "@/queries/runner-configuration-queries";
import { useListRunners, type PlainRunner } from "@/queries/runner-queries";
import { useMemo } from "react";

export type RunnerWithUsage = {
    runner: PlainRunner;
    projectCount: number;
    environmentCount: number;
    hasMoreEnvironments: boolean;
};

/**
 * Hook to fetch all runners that use the canonical domain (e.g., app.gitpod.io)
 * along with their usage statistics (project count and environment count).
 */
export const useCanonicalDomainRunners = (canonicalDomain: string) => {
    const { runners, isLoading: isLoadingRunners } = useListRunners({});
    const { data: projectsData, isPending: isPendingProjects } = useListProjects();
    const { data: projectsStates, isLoading: isLoadingProjectsStates } = useListProjectStates(
        projectsData?.projects.map((p) => p.id),
    );

    // Filter runners that use the canonical domain
    const canonicalDomainRunners = useMemo(() => {
        if (!runners) return [];

        return runners.filter((runner) => {
            const apiEndpoint = runner.status?.additionalInfo?.find((info) => info.key === "apiEndpoint")?.value;

            if (!apiEndpoint) return false;

            try {
                const url = new URL(apiEndpoint);
                return url.hostname === canonicalDomain;
            } catch {
                return false;
            }
        });
    }, [runners, canonicalDomain]);

    const isLoading = isLoadingRunners || isPendingProjects || isLoadingProjectsStates;

    return {
        runners: canonicalDomainRunners,
        isLoading,
        projectsStates,
    };
};

/**
 * Hook to get usage statistics for a specific runner
 */
export const useRunnerUsage = (runnerId: string) => {
    const { data: projectsData, isPending: isPendingProjects } = useListProjects();
    const { data: projectsStates, isLoading: isLoadingProjectsStates } = useListProjectStates(
        projectsData?.projects.map((p) => p.id),
    );
    const { data: environmentClasses } = useListRunnerEnvironmentClasses(runnerId);

    const {
        data: environmentsData,
        hasNextPage: hasNextPageEnvironments,
        isPending: isPendingEnvironments,
    } = useListEnvironmentInventory({ runnerID: runnerId });

    // Calculate project count
    const numProjects = useMemo(() => {
        if (!projectsStates || !environmentClasses) return 0;

        const environmentClassIds = environmentClasses.map((ec) => ec.id);
        return projectsStates.filter((p) => {
            return p.environmentClasses?.some(
                (ec) =>
                    ec.environmentClass.case === "environmentClassId" &&
                    environmentClassIds.includes(ec.environmentClass.value),
            );
        }).length;
    }, [projectsStates, environmentClasses]);

    // Calculate environment count
    const numEnvironments = useMemo(() => {
        return environmentsData?.pages.flatMap((page) => page.environments).length ?? 0;
    }, [environmentsData]);

    return {
        projectCount: numProjects,
        environmentCount: numEnvironments,
        hasMoreEnvironments: hasNextPageEnvironments ?? false,
        isLoading: isPendingProjects || isLoadingProjectsStates || isPendingEnvironments,
    };
};
